import { useEffect, useMemo, useRef, useState } from 'react';
import { IoAdd, IoChevronBack, IoChevronForward, IoClose, IoRemove } from 'react-icons/io5';
import useCartStore from "../../states/CartStore";
import { getProductGalleryImages, getProductVariantImage } from "../../states/ProductStore";
import { addToCart } from "../../services/OnlineStore";
import styles from './ProductCard.module.css';

const normalizeAttributeName = (value) => String(value ?? '').trim().toLowerCase();
const EMPTY_PRODUCT = {};

function normalizeColorCode(attribute) {
  const colorCode = String(
    attribute?.color_code ?? attribute?.colorCode ?? attribute?.ColorCode ?? ''
  ).trim();

  return /^#[0-9a-f]{6}$/i.test(colorCode) ? colorCode : '';
}

function isRealAttributeValue(name, value) {
  const normalizedName = normalizeAttributeName(name);
  const normalizedValue = String(value ?? '').trim();
  if (!normalizedValue) return false;
  if (normalizedName === 'size' && normalizedValue.toLowerCase() === 'size') return false;
  if (normalizedName === 'color' && normalizedValue.toLowerCase() === 'color') return false;
  return true;
}

function getVariantAttributeValue(variant, name) {
  return (variant?.attributes ?? []).find(
    (attribute) => normalizeAttributeName(attribute.name) === name
  )?.value;
}

function isVariantInStock(variant) {
  return Number(variant?.stock) > 0;
}

function SizePicker({ productName, sizes, availableSizes, selectedSize, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const pickerRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closePicker = (event) => {
      if (event.key === 'Escape' || !pickerRef.current?.contains(event.target)) {
        setIsOpen(false);
        if (event.key === 'Escape') triggerRef.current?.focus();
      }
    };

    document.addEventListener('keydown', closePicker);
    document.addEventListener('mousedown', closePicker);
    return () => {
      document.removeEventListener('keydown', closePicker);
      document.removeEventListener('mousedown', closePicker);
    };
  }, [isOpen]);

  const moveFocus = (event) => {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    setHasOpened(true);
    setIsOpen(true);

    requestAnimationFrame(() => {
      const options = [...pickerRef.current.querySelectorAll('[role="option"]:not(:disabled)')];
      const currentIndex = options.indexOf(document.activeElement);
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = currentIndex < 0
        ? (direction > 0 ? 0 : options.length - 1)
        : (currentIndex + direction + options.length) % options.length;
      options[nextIndex]?.focus();
    });
  };

  return (
    <div className={styles.sizePicker} data-open={isOpen} data-visited={hasOpened} ref={pickerRef} onKeyDown={moveFocus}>
      <span>Size</span>
      <div className={styles.sizeSelect}>
        <button
          ref={triggerRef}
          className={styles.sizeSelectButton}
          type="button"
          aria-label={`Select size for ${productName}`}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={() => {
            setHasOpened(true);
            setIsOpen((current) => !current);
          }}
        >
          <span>{selectedSize || 'N/A'}</span>
          <span className={styles.sizeSelectArrow} aria-hidden="true">
            <IoChevronForward className={styles.sizeSelectChevron} />
          </span>
          <svg className={styles.sizeSelectEdge} viewBox="0 0 20 40" preserveAspectRatio="none" aria-hidden="true"><path d="M10 0 Q10 20 10 40" /></svg>
        </button>
        <div className={styles.sizeMenu} role="listbox" aria-label={`Sizes for ${productName}`} aria-hidden={!isOpen} inert={!isOpen}>
          {sizes.map((size) => {
            if (size === selectedSize) return null;
            const isAvailable = availableSizes.has(size);

            return (
                <button
                  className={`${styles.sizeOption} ${!isAvailable ? styles.sizeOptionUnavailable : ''}`}
                  type="button"
                  role="option"
                  aria-selected={selectedSize === size}
                  disabled={!isAvailable}
                  key={size}
                  onClick={() => {
                    onChange(size);
                    setIsOpen(false);
                    triggerRef.current?.focus();
                  }}
                >
                  {size}
                </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function ProductCard({ product, openSizeGuide, index = 0 }) {
  const { setCart } = useCartStore.getState();
  const safeProduct = product ?? EMPTY_PRODUCT;
  const normalizedProductName = normalizeAttributeName(safeProduct.name);
  const productMediaClass = /quarter|q-?zip/.test(normalizedProductName)
    ? styles.quarterZipProduct
    : /short/.test(normalizedProductName) ? styles.shortsProduct : '';
  const lightboxRef = useRef(null);
  const variants = useMemo(
    () => Array.isArray(safeProduct.variants) ? safeProduct.variants : [],
    [safeProduct.variants]
  );
  const isAlternatingLayout = index % 2 === 1;
  const variantAttributes = useMemo(
    () => variants.flatMap((variant) => variant.attributes ?? []),
    [variants]
  );

  const sizes = useMemo(() => [...new Set(
    variantAttributes
      .filter((attribute) => normalizeAttributeName(attribute.name) === 'size' && isRealAttributeValue(attribute.name, attribute.value))
      .map((attribute) => attribute.value)
  )], [variantAttributes]);

  const colorOptions = useMemo(() => {
    const optionsByName = new Map();

    variantAttributes
      .filter((attribute) => normalizeAttributeName(attribute.name) === 'color' && isRealAttributeValue(attribute.name, attribute.value))
      .forEach((attribute) => {
        const name = String(attribute.value).trim();
        const colorCode = normalizeColorCode(attribute);
        const current = optionsByName.get(name);

        if (!current || (!current.colorCode && colorCode)) {
          optionsByName.set(name, { name, colorCode });
        }
      });

    return [...optionsByName.values()];
  }, [variantAttributes]);

  const colors = useMemo(() => colorOptions.map((color) => color.name), [colorOptions]);

  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (!colors.length) {
      setSelectedColor('');
      return;
    }
    setSelectedColor((current) => (colors.includes(current) ? current : colors[0]));
  }, [colors]);

  const availableSizes = useMemo(() => new Set(
    variants
      .filter((variant) => (
        isVariantInStock(variant)
        && getVariantAttributeValue(variant, 'color') === selectedColor
      ))
      .map((variant) => getVariantAttributeValue(variant, 'size'))
      .filter(Boolean)
  ), [selectedColor, variants]);

  useEffect(() => {
    if (!sizes.length) {
      setSelectedSize('');
      return;
    }

    setSelectedSize((current) => (
      availableSizes.has(current) ? current : sizes.find((size) => availableSizes.has(size)) ?? ''
    ));
  }, [availableSizes, sizes]);

  const selectedVariant = variants.find((variant) =>
    getVariantAttributeValue(variant, 'size') === selectedSize
    && getVariantAttributeValue(variant, 'color') === selectedColor
  );
  const selectedVariantInStock = isVariantInStock(selectedVariant);

  const selectedImageUrl = useMemo(
    () => getProductVariantImage(safeProduct, selectedVariant, selectedColor),
    [safeProduct, selectedVariant, selectedColor]
  );
  const galleryImages = useMemo(
    () => getProductGalleryImages(safeProduct, selectedColor, selectedSize),
    [safeProduct, selectedColor, selectedSize]
  );
  const imageUrl = galleryImages[activeImageIndex] || selectedImageUrl;
  const hasMultipleImages = galleryImages.length > 1;

  useEffect(() => {
    const selectedIndex = galleryImages.indexOf(selectedImageUrl);
    setActiveImageIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [galleryImages, selectedImageUrl]);

  useEffect(() => {
    const dialog = lightboxRef.current;
    if (isLightboxOpen && dialog && !dialog.open) dialog.showModal();
  }, [isLightboxOpen]);

  useEffect(() => {
    if (!isLightboxOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsLightboxOpen(false);
        setZoom(1);
        return;
      }
      if (event.key === 'ArrowLeft') {
        setActiveImageIndex((current) => (current - 1 + galleryImages.length) % galleryImages.length);
        setZoom(1);
      }
      if (event.key === 'ArrowRight') {
        setActiveImageIndex((current) => (current + 1) % galleryImages.length);
        setZoom(1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [galleryImages.length, isLightboxOpen]);

  const showPreviousImage = (event) => {
    event?.stopPropagation();
    setActiveImageIndex((current) => (current - 1 + galleryImages.length) % galleryImages.length);
    setZoom(1);
  };

  const showNextImage = (event) => {
    event?.stopPropagation();
    setActiveImageIndex((current) => (current + 1) % galleryImages.length);
    setZoom(1);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setZoom(1);
  };
  const displayPrice = selectedVariant
    ? `$${(selectedVariant.effective_price_cents ?? selectedVariant.price ?? 0) / 100}`
    : safeProduct.pricing?.display || '—';
  const hasVariantControls = sizes.length > 0 || colors.length > 0;

  const handleAddToCart = async () => {
    if (!selectedVariant || !selectedVariantInStock) {
      console.warn('Add to cart skipped: no valid variant selected for product', safeProduct?.id);
      return;
    }

    try {
      const cartToken = localStorage.getItem('cartToken');
      const payload = {
        cart_token: cartToken,
        variant_id: selectedVariant.id,
        quantity: 1,
      };

      const response = await addToCart(payload);
      const nextCart = response?.cart ?? response ?? null;

      if (nextCart) {
        setCart(nextCart);
        if (nextCart.cart_token) {
          localStorage.setItem('cartToken', nextCart.cart_token);
        }
      }
    } catch (error) {
      console.error('Failed to add item to cart:', error);
    }
  };

  return (
    <article className={`${styles.garment} ${isAlternatingLayout ? styles.garmentSweater : styles.garmentSpread} ${productMediaClass} ${styles.editorialImage} reveal`}>
      <div className={`${styles.garmentImage} ${styles.garmentImageSpread}`}>
        {imageUrl ? (
          <button className={styles.productImageButton} type="button" onClick={() => setIsLightboxOpen(true)} aria-label={`Open ${safeProduct.name || 'product'} image gallery`}>
            <img
              className={styles.productImage}
              src={imageUrl}
              alt={safeProduct.name || 'Product'}
            />
          </button>
        ) : (
          <div className={styles.figTag}>{(safeProduct.name || 'M').slice(0, 1).toUpperCase()}</div>
        )}
        {hasMultipleImages && (
          <div className={styles.galleryControls} aria-label={`${safeProduct.name} image navigation`}>
            <button type="button" onClick={showPreviousImage} aria-label="Previous image"><IoChevronBack /></button>
            <span>{activeImageIndex + 1} / {galleryImages.length}</span>
            <button type="button" onClick={showNextImage} aria-label="Next image"><IoChevronForward /></button>
          </div>
        )}
      </div>
      <div className={styles.garmentDetail}>
        <div className={styles.garmentCopy}>
          <h3 className={styles.garmentName}>{safeProduct.name || 'Untitled product'}</h3>
          <p className={styles.garmentDesc}>{safeProduct.description || 'No description available.'}</p>

          {hasVariantControls && (
            <div className={styles.colorwayPicker}>
              <span className={styles.pickerLabel}>Color</span>
              <div className={styles.colorwayOptions}>
                {colorOptions.length > 0 ? colorOptions.map(({ name, colorCode }) => {
                  const isCamo = normalizeAttributeName(name) === 'camo';

                  return (
                    <label key={name} className={styles.colorwayOption}>
                      <input
                        type="radio"
                        name={`${safeProduct.id}-color`}
                        value={name}
                        checked={selectedColor === name}
                        onChange={(event) => setSelectedColor(event.target.value)}
                      />
                      <span
                        className={`${styles.swatch} ${isCamo ? styles.swatchCamo : ''}`}
                        style={isCamo ? undefined : { backgroundColor: colorCode || name }}
                      />
                      {name}
                    </label>
                  );
                }) : <span>—</span>}
              </div>
            </div>
          )}

          {hasVariantControls && (
            <div className={styles.productActions}>
              <button className={styles.addToCart} type="button" onClick={handleAddToCart} disabled={!selectedVariantInStock}>Add to cart</button>
              <span className={styles.productPrice}>{displayPrice}</span>
              <SizePicker
                productName={safeProduct.name}
                sizes={sizes}
                availableSizes={availableSizes}
                selectedSize={selectedSize}
                onChange={setSelectedSize}
              />
            </div>
          )}

          {!hasVariantControls && (
            <div className={styles.productActions}>
              <button className={styles.addToCart} type="button" onClick={handleAddToCart}>Add to cart</button>
              <span className={styles.productPrice}>{displayPrice}</span>
            </div>
          )}
          <button className={styles.sizingGuideTrigger} type="button" onClick={() => openSizeGuide?.(safeProduct, selectedSize)}>
            <span className={styles.sizingGuideTitle}>Sizing guide <small>Inches</small></span><span className={styles.sizingGuideChevron} aria-hidden="true"></span>
          </button>
        </div>
      </div>
      {isLightboxOpen && (
        <dialog ref={lightboxRef} className={styles.lightbox} aria-label={`${safeProduct.name} image gallery`} onClose={closeLightbox} onClick={(event) => event.target === lightboxRef.current && closeLightbox()}>
          <header className={styles.lightboxHeader}>
            <div>
              <strong>{safeProduct.name}</strong>
              <span>{activeImageIndex + 1} / {galleryImages.length}</span>
            </div>
            <div className={styles.lightboxTools}>
              <button type="button" onClick={() => setZoom((current) => Math.max(1, current - 0.5))} disabled={zoom === 1} aria-label="Zoom out"><IoRemove /></button>
              <span>{Math.round(zoom * 100)}%</span>
              <button type="button" onClick={() => setZoom((current) => Math.min(3, current + 0.5))} disabled={zoom === 3} aria-label="Zoom in"><IoAdd /></button>
              <button type="button" onClick={closeLightbox} aria-label="Close image gallery"><IoClose /></button>
            </div>
          </header>
          <div className={styles.lightboxStage}>
            {hasMultipleImages && <button className={`${styles.lightboxArrow} ${styles.lightboxPrevious}`} type="button" onClick={showPreviousImage} aria-label="Previous image"><IoChevronBack /></button>}
            <div className={styles.lightboxImageViewport}>
              <img src={imageUrl} alt={`${safeProduct.name} view ${activeImageIndex + 1}`} style={{ transform: `scale(${zoom})` }} onClick={() => setZoom((current) => current === 1 ? 2 : 1)} />
            </div>
            {hasMultipleImages && <button className={`${styles.lightboxArrow} ${styles.lightboxNext}`} type="button" onClick={showNextImage} aria-label="Next image"><IoChevronForward /></button>}
          </div>
        </dialog>
      )}
    </article>
  );
}